import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import ToggleSwitch from "../../../components/Settings/suppliers/ToggleSwitch";
import Button1 from "../../../components/form/Button1";
import agentSupplierSettingController from "../../../controllers/settings/supplier/agentSupplierSettingController";
import getSuppliersList from "../../../controllers/settings/supplier/getSuppliersList";
import getSuppliersName from "../../../controllers/settings/supplier/getFlightSetting";
import {
  setGlobalEnabledSuppliers,
  setSupplierList,
} from "../../../redux/reducers/settings/supplierSettingsSlice";
import { ListShimmer } from "../../../components/common/reportDashBShimmer/ListShimmer";

function agentDisplayName(agent) {
  const fullName = [agent?.firstName, agent?.lastName]
    .filter(Boolean)
    .join(" ");
  return (
    agent?.detail?.agencyName ||
    agent?.detail?.companyName ||
    agent?.detail?.tradingName ||
    fullName ||
    agent?.email ||
    "Agent"
  );
}

function buildEnabledMap(suppliers, enabledSuppliers = []) {
  const enabledSet = new Set(
    enabledSuppliers.map((item) => String(item).trim()).filter(Boolean),
  );
  const next = {};
  suppliers.forEach((supplier) => {
    const key = supplier?.value || supplier?.name;
    if (!key) return;
    next[key] =
      enabledSet.has(key) ||
      enabledSet.has(supplier?.value) ||
      enabledSet.has(supplier?.name);
  });
  return next;
}

function normalizeSupplierKeys(arr = []) {
  return [
    ...new Set(arr.map((item) => String(item).trim()).filter(Boolean)),
  ].sort();
}

function areSupplierListsEqual(a, b) {
  const left = normalizeSupplierKeys(a);
  const right = normalizeSupplierKeys(b);
  return (
    left.length === right.length && left.every((val, idx) => val === right[idx])
  );
}

function supplierLabel(suppliers, key) {
  const match = suppliers.find(
    (supplier) => supplier?.value === key || supplier?.name === key,
  );
  return match?.name || match?.value || key;
}

function SupplierChipList({ suppliers, keys, emptyLabel }) {
  if (!keys.length) {
    return <span className="text-xs text-[#6D7C94] italic">{emptyLabel}</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {keys.map((key) => (
        <span
          key={key}
          className="inline-flex items-center rounded-full border border-[#E2E8F0] bg-white px-2.5 py-1 text-xs font-medium text-[#1A1A1A]"
        >
          {supplierLabel(suppliers, key)}
        </span>
      ))}
    </div>
  );
}

function SupplierComparisonCard({
  suppliers,
  parentKeys,
  agentKeys,
  isSavedDifferent,
  onDelete,
}) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 flex flex-col gap-3">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <Icon icon="mdi:compare-horizontal" className="text-lg" />
        </span>
        <div className="min-w-0">
          <span className="block text-sm font-semibold text-amber-900">
            Custom supplier override
          </span>
          <span className="mt-0.5 block text-xs text-amber-800/80">
            This agent&apos;s suppliers differ from the global default.
            {isSavedDifferent
              ? " Delete to revert the agent back to global settings."
              : " Save to apply or reset changes to match global."}
          </span>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-[#E2E8F0] bg-white p-3">
          <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#6D7C94]">
            Global default
          </span>
          <SupplierChipList
            suppliers={suppliers}
            keys={parentKeys}
            emptyLabel="No global suppliers enabled"
          />
        </div>
        <div className="rounded-lg border border-amber-300 bg-white p-3">
          <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-amber-700">
            Agent override
          </span>
          <SupplierChipList
            suppliers={suppliers}
            keys={agentKeys}
            emptyLabel="No agent suppliers enabled"
          />
        </div>
      </div>

      {isSavedDifferent && onDelete ? (
        <div className="flex justify-end border-t border-amber-200/80 pt-3">
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50"
          >
            <Icon icon="mdi:trash-can-outline" className="text-lg" />
            Delete override
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function AgentSupplierConfigModal({
  open,
  agent,
  onBack,
  onClose,
}) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { supplierList, globalEnabledSuppliers } = useSelector(
    (state) => state.supplierSettings,
  );
  const [savedEnabledSuppliers, setSavedEnabledSuppliers] = useState([]);
  const [toggleOverrides, setToggleOverrides] = useState({});
  const [existingSetting, setExistingSetting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const suppliers = useMemo(
    () => (Array.isArray(supplierList) ? supplierList : []),
    [supplierList],
  );

  const enabledMap = useMemo(() => {
    const base = buildEnabledMap(suppliers, savedEnabledSuppliers);
    return { ...base, ...toggleOverrides };
  }, [suppliers, savedEnabledSuppliers, toggleOverrides]);

  const enabledSuppliers = useMemo(
    () =>
      Object.entries(enabledMap)
        .filter(([, enabled]) => Boolean(enabled))
        .map(([key]) => key),
    [enabledMap],
  );

  const parentEnabledSuppliers = useMemo(
    () => (Array.isArray(globalEnabledSuppliers) ? globalEnabledSuppliers : []),
    [globalEnabledSuppliers],
  );

  const parentEnabledMap = useMemo(
    () => buildEnabledMap(suppliers, parentEnabledSuppliers),
    [suppliers, parentEnabledSuppliers],
  );

  const parentEnabledKeys = useMemo(
    () =>
      suppliers
        .map((supplier) => supplier?.value || supplier?.name)
        .filter((key) => key && parentEnabledMap[key]),
    [suppliers, parentEnabledMap],
  );

  const isSavedDifferentFromParent = useMemo(
    () => !areSupplierListsEqual(savedEnabledSuppliers, parentEnabledSuppliers),
    [savedEnabledSuppliers, parentEnabledSuppliers],
  );

  const isCurrentlyDifferentFromParent = useMemo(
    () => !areSupplierListsEqual(enabledSuppliers, parentEnabledSuppliers),
    [enabledSuppliers, parentEnabledSuppliers],
  );

  const hasEnabledSupplier = enabledSuppliers.length > 0;
  const hasExistingSetting =
    Boolean(existingSetting) || savedEnabledSuppliers.length > 0;
  const showDelete = isSavedDifferentFromParent && hasExistingSetting;
  const showComparison = isCurrentlyDifferentFromParent;

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;

    const loadSuppliers = async () => {
      if (suppliers.length === 0) {
        const res = await getSuppliersList();
        if (!cancelled && res?.return && Array.isArray(res?.data)) {
          dispatch(setSupplierList(res.data));
        }
      }

      if (parentEnabledSuppliers.length === 0) {
        const res = await getSuppliersName();
        if (
          !cancelled &&
          res?.return &&
          Array.isArray(res?.data?.enabledSuppliers)
        ) {
          dispatch(setGlobalEnabledSuppliers(res.data.enabledSuppliers));
        }
      }
    };

    loadSuppliers();

    return () => {
      cancelled = true;
    };
  }, [open, suppliers.length, parentEnabledSuppliers.length, dispatch]);

  useEffect(() => {
    if (!open || !agent?._id) return undefined;

    let cancelled = false;

    const loadSetting = async () => {
      setLoading(true);
      setExistingSetting(null);
      setConfirmDeleteOpen(false);
      setSavedEnabledSuppliers([]);
      setToggleOverrides({});

      const res = await agentSupplierSettingController.getSetting(agent._id);
      if (cancelled) return;

      if (res?.return && res?.data) {
        const savedEnabled = Array.isArray(res?.data?.enabledSuppliers)
          ? res.data.enabledSuppliers
          : [];
        setSavedEnabledSuppliers(savedEnabled);
        setExistingSetting(res.data);
      } else {
        setSavedEnabledSuppliers([]);
        setExistingSetting(null);
      }
      setLoading(false);
    };

    loadSetting();

    return () => {
      cancelled = true;
    };
  }, [open, agent?._id]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open || !agent) return null;

  const name = agentDisplayName(agent);
  const contactName = [agent?.firstName, agent?.lastName]
    .filter(Boolean)
    .join(" ");
  const accountId = agent?._id;

  const handleToggle = (key, value) => {
    setToggleOverrides((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!accountId || !hasEnabledSupplier) return;

    setSaving(true);
    const res = await agentSupplierSettingController.upsertSetting(accountId, {
      enabledSuppliers,
    });
    setSaving(false);

    if (res?.return) {
      setSavedEnabledSuppliers(enabledSuppliers);
      setToggleOverrides({});
      enqueueSnackbar(res?.msg || "Supplier settings saved", {
        variant: "success",
      });
      onBack?.();
    } else {
      enqueueSnackbar(res?.msg || "Unable to save supplier settings", {
        variant: "error",
      });
    }
  };

  const handleDelete = async () => {
    if (!accountId || !showDelete) return;

    setDeleting(true);
    const res = await agentSupplierSettingController.deleteSetting(accountId);
    setDeleting(false);

    if (res?.return) {
      enqueueSnackbar(res?.msg || "Supplier settings deleted", {
        variant: "success",
      });
      setConfirmDeleteOpen(false);
      setExistingSetting(null);
      setSavedEnabledSuppliers([]);
      setToggleOverrides({});
      onBack?.();
    } else {
      enqueueSnackbar(res?.msg || "Unable to delete supplier settings", {
        variant: "error",
      });
    }
  };

  const configPortal = ReactDOM.createPortal(
    <div className="fixed inset-0 z-[60] flex justify-end">
      <button
        type="button"
        aria-label="Close agent supplier config"
        className="absolute inset-0 bg-black/40 border-0 cursor-pointer"
        onClick={onClose}
      />

      <aside
        className="relative z-[1] flex h-full w-full sm:w-[520px] md:w-[640px] max-w-full flex-col bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label="Configure agent suppliers"
      >
        <div className="flex items-center justify-between gap-2 border-b border-[#E2E8F0] bg-[#0B2B4A] p-4 text-white shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={onBack}
              className="shrink-0 rounded p-1 text-white hover:bg-white/10"
              aria-label="Back to agents"
            >
              <Icon icon="material-symbols:arrow-back" className="text-2xl" />
            </button>
            <div className="flex flex-col gap-0.5 min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-white m-0 truncate">
                {name}
              </h2>
              <span className="text-xs sm:text-sm text-white/80 truncate">
                {contactName && contactName !== name ? `${contactName} · ` : ""}
                {agent?.email || "Supplier settings"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded p-1 text-white hover:bg-white/10"
              aria-label="Close"
            >
              <Icon icon="material-symbols:close" className="text-2xl" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 pb-28">
          <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-3">
              <span className="text-sm text-[#6D7C94] m-0 block">
                Enable suppliers for this agent, then save.
              </span>
              {!loading && showDelete ? (
                <button
                  type="button"
                  onClick={() => setConfirmDeleteOpen(true)}
                  className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-600"
                >
                  <Icon icon="mdi:trash-can-outline" className="text-lg" />
                  Delete override
                </button>
              ) : null}
            </div>

            {!loading && showComparison ? (
              <SupplierComparisonCard
                suppliers={suppliers}
                parentKeys={parentEnabledKeys}
                agentKeys={enabledSuppliers}
                isSavedDifferent={isSavedDifferentFromParent}
                onDelete={
                  showDelete ? () => setConfirmDeleteOpen(true) : undefined
                }
              />
            ) : null}

            {!loading &&
            hasExistingSetting &&
            !isCurrentlyDifferentFromParent ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2">
                <Icon
                  icon="mdi:check-circle-outline"
                  className="text-lg text-emerald-600 shrink-0"
                />
                <span className="text-sm text-emerald-800">
                  Agent suppliers match the global default.
                </span>
              </div>
            ) : null}

            {loading ? (
              <ListShimmer rows={6} />
            ) : suppliers.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[#DBDBDB] px-4 py-10 text-center">
                <span className="text-sm text-[#6D7C94] m-0 block">
                  No suppliers loaded yet. Open the Suppliers settings page to
                  load them first.
                </span>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {suppliers.map((supplier) => {
                  const key = supplier?.value || supplier?.name;
                  const parentEnabled = Boolean(parentEnabledMap[key]);
                  const agentEnabled = Boolean(enabledMap[key]);
                  const isRowDifferent = parentEnabled !== agentEnabled;

                  return (
                    <div
                      key={key}
                      className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 ${
                        isRowDifferent
                          ? "border-amber-300 bg-amber-50/60"
                          : "border-[#E2E8F0]"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {supplier?.icon ? (
                          <img
                            src={supplier.icon}
                            alt=""
                            className="w-8 h-8 object-contain rounded"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded bg-[#F0F6FF] flex items-center justify-center shrink-0">
                            <Icon
                              icon="mdi:airplane"
                              className="text-lg text-primary1"
                            />
                          </div>
                        )}
                        <div className="flex flex-col min-w-0 gap-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-semibold text-[#1A1A1A] truncate">
                              {supplier?.name || supplier?.value}
                            </span>
                            {isRowDifferent ? (
                              <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                                Custom
                              </span>
                            ) : null}
                          </div>
                          {supplier?.value ? (
                            <span className="text-xs text-[#6D7C94] truncate">
                              {supplier.value}
                            </span>
                          ) : null}
                          <span className="text-[11px] text-[#6D7C94]">
                            Global default:{" "}
                            <span
                              className={
                                parentEnabled
                                  ? "font-medium text-emerald-700"
                                  : "font-medium text-slate-500"
                              }
                            >
                              {parentEnabled ? "Enabled" : "Disabled"}
                            </span>
                          </span>
                        </div>
                      </div>
                      <ToggleSwitch
                        toggleValue={agentEnabled}
                        callback={(val) => handleToggle(key, val)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {!loading && hasEnabledSupplier ? (
          <div className="shrink-0 sticky bottom-0 z-10 border-t border-[#E2E8F0] bg-white p-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
            <div className="flex items-center justify-end gap-3">
              <span className="text-sm text-[#6D7C94] mr-auto">
                {enabledSuppliers.length} supplier
                {enabledSuppliers.length === 1 ? "" : "s"} selected
              </span>
              <Button1
                className="btn-theme !rounded-md !w-1/2 !px-8 !py-2.5 !text-base"
                loading={saving}
                disabled={saving || deleting}
                onClick={handleSave}
              >
                Save
              </Button1>
            </div>
          </div>
        ) : null}
      </aside>
    </div>,
    document.body,
  );

  return (
    <>
      {configPortal}
      {confirmDeleteOpen
        ? ReactDOM.createPortal(
            <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
              <button
                type="button"
                aria-label="Close delete confirmation"
                className="absolute inset-0 bg-black/50 border-0 cursor-pointer"
                onClick={() => !deleting && setConfirmDeleteOpen(false)}
              />
              <div
                className="relative z-[1] bg-white rounded-lg p-6 max-w-md w-full shadow-xl flex flex-col gap-3"
                role="dialog"
                aria-modal="true"
                aria-label="Delete supplier settings confirmation"
              >
                <div className="flex items-start justify-between gap-3">
                  <h5 className="m-0 text-lg font-semibold text-[#1A1A1A]">
                    Delete supplier settings
                  </h5>
                  <button
                    type="button"
                    disabled={deleting}
                    onClick={() => setConfirmDeleteOpen(false)}
                    className="shrink-0 rounded p-1 text-[#6D7C94] hover:bg-gray-100"
                    aria-label="Close"
                  >
                    <Icon icon="material-symbols:close" className="text-xl" />
                  </button>
                </div>
                <span className="m-0 block text-sm text-[#6D7C94]">
                  Delete this agent&apos;s custom supplier override and revert
                  to the global default suppliers?
                </span>
                <div className="flex items-center justify-end gap-3 mt-2">
                  <Button1
                    className="!w-auto btn-theme-light !rounded-md"
                    disabled={deleting}
                    onClick={() => setConfirmDeleteOpen(false)}
                  >
                    Cancel
                  </Button1>
                  <Button1
                    className="!w-auto !bg-red-500 !text-white !rounded-md"
                    loading={deleting}
                    disabled={deleting}
                    onClick={handleDelete}
                  >
                    Delete
                  </Button1>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
